/**
 * Shared question-authoring types, constants and factories.
 *
 * Extracted from `QuizEditor.tsx` (E1) so both the quiz editor and the
 * question-bank item form drive the same per-type sub-editors
 * (`McqOptionsEditor` / `TfAnswerEditor` / `OrderItemsEditor`) from one
 * source of truth. Nothing here changed behaviourally — it is a pure move
 * of the editor's internal model.
 */
export type EditorQuestion =
  | {
      type: 'MultipleChoice'
      text: string
      timeSeconds: number
      options: string[]
      correctOptionIndex: number
      /** Optional explanation shown to players after the reveal. */
      explanation?: string | undefined
    }
  | {
      type: 'TrueFalse'
      text: string
      timeSeconds: number
      correctAnswer: boolean
      /** Optional explanation shown to players after the reveal. */
      explanation?: string | undefined
    }
  | {
      type: 'OrderSequence'
      text: string
      timeSeconds: number
      /** Items stored in their correct order (index 0 = first). */
      items: string[]
      /** Optional explanation shown to players after the reveal. */
      explanation?: string | undefined
    }

export type EditorQuiz = {
  title: string
  description: string
  questions: EditorQuestion[]
}

export const DEFAULT_TIME_SECONDS = 20
export const MIN_TIME_SECONDS = 5
export const MAX_TIME_SECONDS = 120
export const MCQ_OPTION_COUNT = 4
export const ORDER_DEFAULT_ITEM_COUNT = 4
export const ORDER_MIN_ITEMS = 2
export const ORDER_MAX_ITEMS = 8

/**
 * Build an empty MCQ question with the canonical four options and the
 * first option pre-selected as correct (matches Kahoot's default).
 */
export function makeMcq(): EditorQuestion {
  return {
    type: 'MultipleChoice',
    text: '',
    timeSeconds: DEFAULT_TIME_SECONDS,
    options: Array.from({ length: MCQ_OPTION_COUNT }, () => ''),
    correctOptionIndex: 0,
  }
}

/**
 * Build an empty true / false question defaulted to `true` (so the
 * "correct" toggle has a non-null seed value).
 */
export function makeTf(): EditorQuestion {
  return {
    type: 'TrueFalse',
    text: '',
    timeSeconds: DEFAULT_TIME_SECONDS,
    correctAnswer: true,
  }
}

/**
 * Build an empty Order/Sequence question with four blank items. The
 * editor list always represents the *correct* order (top → bottom =
 * first → last); the server shuffles the displayed sequence per-game so
 * players never see the canonical order.
 */
export function makeOrder(): EditorQuestion {
  return {
    type: 'OrderSequence',
    text: '',
    timeSeconds: DEFAULT_TIME_SECONDS,
    items: Array.from({ length: ORDER_DEFAULT_ITEM_COUNT }, () => ''),
  }
}
